/**
 * Paid-traffic attribution params, and how they reach the booking app.
 *
 * Ads land on this site (getdimension.com) but the booking flow lives on
 * app.getdimension.com. Those are different origins, so nothing this site
 * stores — sessionStorage, localStorage — is readable over there. The query
 * string on the link is the only channel between the two, which is why every
 * link into the app has to carry these params explicitly.
 *
 * What the app does with them (trashwarrior-react):
 *  - `gclid` / `gbraid` / `msclkid` are read from the URL on app load, kept in
 *    sessionStorage as `gclid-track` etc. and attached to the cart —
 *    `src/containers/index.tsx`.
 *  - `utm_source` / `utm_medium` / `utm_campaign` / `utm_term` are read from
 *    `window.location.search` when the booking form's engagement tracker mounts
 *    and written onto the cart, which is what links a booking to a campaign —
 *    `src/shared/hooks/useEngagementTracker.tsx`. The app only falls back to its
 *    own sessionStorage for a session it already started, so a first arrival
 *    with no params in the URL is simply unattributed.
 *  - `keyword` / `location` select the keyword doc and the geo target.
 *  - `deviceId` is not an ad param: it is this visitor's Amplitude device id,
 *    which the app's Amplitude browser SDK (2.x) adopts on init over its own
 *    cookie. That puts the site and app events on one Amplitude user, so a
 *    site → app funnel (e.g. an A/B arm → form steps) is joinable per visitor.
 * Keep these in sync with those two files.
 *
 * Everything else on an ad URL — the `hsa_*` block Google auto-tagging adds,
 * `gad_*`, the ValueTrack names — the app does not read today but the ads team
 * reconciles against, so we forward it rather than curate it.
 */
import { getAnonymousId } from '@src/functions/amplitude'

export const ATTRIBUTION_PARAMS = [
  // Click identifiers.
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'msclkid',
  'fbclid',
  'ttclid',
  // Read by the booking flow itself.
  'keyword',
  'location',
  // Competitor-specific ads on /dimension-vs-competition — which vendor the ad named.
  'competitor',
  // Google Ads ValueTrack, for accounts that template them in.
  'campaignid',
  'adgroupid',
  'matchtype',
  'network',
  'device',
  'creative',
  'placement',
] as const

/**
 * Whole families we forward without naming each member: `utm_*`, the `hsa_*`
 * params Google auto-tagging appends (hsa_acc, hsa_cam, hsa_grp, hsa_ad,
 * hsa_src, hsa_tgt, hsa_kw, hsa_mt, hsa_net, hsa_ver), and `gad_*`
 * (gad_source, gad_campaignid). A prefix rule rather than a list so a new
 * member Google starts sending arrives on its own.
 */
export const ATTRIBUTION_PARAM_PREFIXES = ['utm_', 'hsa_', 'gad_'] as const

const isAttributionParam = (key: string): boolean =>
  (ATTRIBUTION_PARAMS as readonly string[]).includes(key) ||
  ATTRIBUTION_PARAM_PREFIXES.some((prefix) => key.startsWith(prefix))

export type Attribution = Record<string, string>

const STORAGE_KEY = 'dimension-attribution'

/**
 * Params arrive empty (`&location&`) or stringified from an undefined React
 * value (`&gclid=undefined`) often enough that both have to count as absent —
 * otherwise we would store junk and, worse, treat a placeholder already on a
 * link as a real value worth preserving.
 */
const isMeaningful = (value: string | null | undefined): value is string =>
  !!value && value !== 'undefined' && value !== 'null'

const readStored = (): Attribution => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Attribution) : {}
  } catch (error) {
    return {}
  }
}

/**
 * Merge whatever attribution params are on the current URL into the session's
 * record and return the whole record. Params in the URL win; a page without
 * them keeps what an earlier page in the session captured, so the visitor can
 * browse from the ad landing page to a service page and still arrive at the app
 * attributed.
 */
export const captureAttribution = (search?: string): Attribution => {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(search ?? window.location.search)
  const stored = readStored()
  let changed = false

  params.forEach((rawValue, key) => {
    if (!isAttributionParam(key)) return
    const value = rawValue.trim()
    if (isMeaningful(value) && stored[key] !== value) {
      stored[key] = value
      changed = true
    }
  })

  if (changed) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    } catch (error) {
      // Private-mode storage failures shouldn't take a CTA down with them.
    }
  }

  return stored
}

export const getAttribution = (): Attribution => captureAttribution()

/** True for links that leave this site for the booking app. */
export const isAppUrl = (url: string): boolean => {
  const base = process.env.NEXT_PUBLIC_REDIRECT_URL
  if (base && url.startsWith(base)) return true
  try {
    return new URL(url, window.location.origin).hostname === 'app.getdimension.com'
  } catch (error) {
    return false
  }
}

/**
 * Stamp the session's attribution params onto a link into the booking app.
 *
 * The session's value wins over whatever the link hardcodes. That matters most
 * for `keyword`: a service page's CTA carries a slug of its own
 * (`?keyword=paint`), but for paid traffic the ad's search term is the one the
 * booking has to be attributed to, so it replaces the slug. Organic traffic has
 * no captured keyword and keeps the page's.
 *
 * Server-side this returns the URL untouched: the params live in
 * sessionStorage. Render-time callers should go through `useAppLink` so the
 * href only changes after hydration.
 */
export const withAttribution = (url: string, extra: Attribution = {}): string => {
  if (typeof window === 'undefined' || !url) return url
  const attribution = { ...getAttribution(), ...extra }

  try {
    const target = new URL(url, window.location.origin)

    Object.entries(attribution).forEach(([key, value]) => {
      target.searchParams.set(key, value)
    })

    // Hand-rolled links carry placeholders like `&gclid=undefined&location=`.
    // Anything the session didn't fill in above stays a placeholder, so drop
    // it: the app reads these with URLSearchParams and would otherwise store
    // the literal string "undefined" as a click id.
    Array.from(target.searchParams.keys()).forEach((key) => {
      if (isAttributionParam(key) && !isMeaningful(target.searchParams.get(key))) {
        target.searchParams.delete(key)
      }
    })

    const deviceId = getAnonymousId()
    if (deviceId) target.searchParams.set('deviceId', deviceId)

    return target.toString()
  } catch (error) {
    return url
  }
}
